/**
 * CORE-BR — Weather Operational Intelligence · per-bay advisory
 *
 * > **The shop declares what weather matters. The platform reports whether
 * > today crosses that line — and never crosses it on the shop's behalf.**
 *
 * This module turns one shop-level assessment into per-bay advisories for
 * the surfaces where people place and manage work. It is the consumer the
 * capability has been missing: the evaluation existed and nothing read it.
 *
 * What this module deliberately does NOT do
 * -----------------------------------------
 * It does not filter. `advisoriesForBays` returns an entry for **every bay
 * it is given**, in the order it was given them, and there is no code path
 * that drops one. The pre-existing `filterOutdoorBays()` helper — which
 * removed outdoor bays from a candidate list outright — is exactly the
 * autonomous block the constitution forbids, and importing it into
 * assignment would have made weather a scheduling authority rather than an
 * operational report.
 *
 * The distinction that matters: the system may say *"this conflicts with
 * your shop's policy, and here is why"*. It may not make choosing that bay
 * impossible. A shop owner who decides the job is fine in 31°F is exercising
 * judgement the platform does not have.
 *
 * Purity: no I/O, no clock, no React. `nowIso` is a parameter.
 */

import type {
  OutdoorWorkAssessment,
  PolicyConflictReason,
  WeatherFreshness,
} from '../../../supabase/functions/_shared/weatherEvidence';

export type {
  OutdoorWorkAssessment,
  PolicyConflictReason,
  WeatherFreshness,
};

/**
 * The minimum a bay must tell us. `is_outdoor` is `shop_bays.is_outdoor`,
 * the shop's own designation, edited in the floor editor.
 */
export interface AdvisoryBay {
  id: string;
  name?: string | null;
  /**
   * `null` means the shop has never designated this bay either way. That is
   * not "indoor" — it is unstated — and an unstated bay gets no outdoor
   * advisory, because inferring one from a bay's name or type would be
   * CORE-BR asserting a fact about the shop's building.
   */
  is_outdoor?: boolean | null;
}

/**
 * What a surface should show for one bay.
 *
 *   - `'none'`      — nothing to say. Indoor, undesignated, or policy off.
 *   - `'advisory'`  — the shop's own policy conflicts with the reading.
 *   - `'unverified'`— weather could not be checked, or the last reading has
 *                     stopped being current. Shown so the reader
 *                     knows the absence of a warning is not a clean bill of
 *                     health.
 */
export type BayAdvisoryLevel = 'none' | 'advisory' | 'unverified';

export interface BayWeatherAdvisory {
  bayId: string;
  bayName: string | null;
  isOutdoor: boolean;
  level: BayAdvisoryLevel;
  /** The sentence to show. Null when there is nothing to say. */
  message: string | null;
  reason: PolicyConflictReason | null;
  freshness: WeatherFreshness | null;
  /**
   * Always true when a message exists. The bay remains selectable; this
   * flag exists so a consumer cannot read an advisory as a prohibition
   * without deliberately ignoring the word.
   */
  selectable: true;
}

const NO_ADVISORY = (bay: AdvisoryBay): BayWeatherAdvisory => ({
  bayId: bay.id,
  bayName: bay.name ?? null,
  isOutdoor: bay.is_outdoor === true,
  level: 'none',
  message: null,
  reason: null,
  freshness: null,
  selectable: true,
});

/**
 * Build the advisory for one bay from the shop-level assessment.
 *
 * Indoor and undesignated bays are never given a weather advisory: outdoor
 * policy is about being outdoors, and warning someone about the temperature
 * in a heated building would be noise that trains people to ignore the
 * signal that matters.
 */
export function advisoryForBay(
  bay: AdvisoryBay,
  assessment: OutdoorWorkAssessment,
): BayWeatherAdvisory {
  if (bay.is_outdoor !== true) return NO_ADVISORY(bay);

  switch (assessment.state) {
    case 'disabled':
      // The shop has not asked for this. Silence is correct.
      return NO_ADVISORY(bay);

    case 'clear':
      return NO_ADVISORY(bay);

    case 'unavailable':
      // Surfaced rather than swallowed. If the reader sees no warning they
      // are entitled to know whether that means "checked and fine" or
      // "could not check" — those were the same thing before this work.
      return {
        ...NO_ADVISORY(bay),
        level: 'unverified',
        message: assessment.explanation,
      };

    case 'stale':
      // Deliberately NOT silent, and deliberately NOT an active conflict.
      //
      // Silence would treat an unconfirmed reading exactly like a confirmed
      // clear one, so a shop would stop hearing anything while its last
      // reading aged out. Keeping the conflict would go on asserting today's
      // conditions from hours-old data. Both are the same error — letting a
      // reading speak with more authority than it has.
      return {
        ...NO_ADVISORY(bay),
        level: 'unverified',
        message: assessment.explanation,
        freshness: assessment.freshness,
      };

    case 'policy_conflict':
      return {
        bayId: bay.id,
        bayName: bay.name ?? null,
        isOutdoor: true,
        level: 'advisory',
        message: assessment.explanation,
        reason: assessment.reason,
        freshness: assessment.freshness,
        selectable: true,
      };
  }
}

/**
 * Advise on a list of bays.
 *
 * Returns one entry per input bay, in input order. This is a mapping, never
 * a filter — the returned length always equals the input length, which is
 * the structural guarantee that CORE-BR cannot remove somebody's options.
 */
export function advisoriesForBays(
  bays: ReadonlyArray<AdvisoryBay>,
  assessment: OutdoorWorkAssessment,
): BayWeatherAdvisory[] {
  return (bays ?? []).map((bay) => advisoryForBay(bay, assessment));
}

// =============================================================================
// Display
// =============================================================================

/** Short label for a tight surface, e.g. beside a bay name on the floor. */
export function shortAdvisoryLabel(
  advisory: BayWeatherAdvisory,
): string | null {
  switch (advisory.level) {
    case 'none':
      return null;
    case 'unverified':
      return advisory.freshness === 'stale'
        ? 'Weather not confirmed'
        : 'Weather unverified';
    case 'advisory':
      return 'Weather policy conflict';
  }
}

export const CONFLICT_LABEL: Readonly<Record<PolicyConflictReason, string>> =
  Object.freeze({
    cold: 'Too cold for this shop’s outdoor policy',
    heat: 'Too hot for this shop’s outdoor policy',
    rain: 'Rain conflicts with this shop’s outdoor policy',
    snow: 'Snow conflicts with this shop’s outdoor policy',
    storm: 'Storms conflict with this shop’s outdoor policy',
    wind: 'Wind conflicts with this shop’s outdoor policy',
  });

export const FRESHNESS_LABEL: Readonly<Record<WeatherFreshness, string>> =
  Object.freeze({
    fresh: 'Current reading',
    // Said explicitly, because a stale reading that looks current is worse
    // than no reading at all — it invites a decision on a fact that may have
    // stopped being true hours ago.
    stale: 'Reading is out of date',
    unknown: 'Reading time unknown',
  });

/**
 * How the assessment should read at shop level, above the bay list.
 *
 * Returns `null` when there is genuinely nothing to say, so a surface can
 * render nothing rather than an empty box.
 */
export function summariseAssessment(
  assessment: OutdoorWorkAssessment,
): { level: BayAdvisoryLevel; message: string } | null {
  switch (assessment.state) {
    case 'disabled':
      return null;
    case 'clear':
      return null;
    case 'unavailable':
      return { level: 'unverified', message: assessment.explanation };
    case 'stale':
      return { level: 'unverified', message: assessment.explanation };
    case 'policy_conflict':
      return { level: 'advisory', message: assessment.explanation };
  }
}

/**
 * Whether the assessment rests on a reading that is no longer current.
 *
 * Consumers use this to say "not confirmed" rather than "unavailable" —
 * there IS a reading, it has simply lost the right to describe now.
 */
export function isStaleAssessment(
  assessment: OutdoorWorkAssessment,
): boolean {
  return assessment.state === 'stale';
}

/**
 * Whether a scheduled outdoor item's evidence is a forecast.
 *
 * Consumers use this to label future work honestly. A forecast may inform a
 * conversation about tomorrow; it may never be shown as something OptiBay
 * observed, and it is never persisted as one.
 */
export function isForecastAssessment(
  assessment: OutdoorWorkAssessment,
): boolean {
  return (
    (assessment.state === 'clear' || assessment.state === 'policy_conflict') &&
    assessment.evidence.kind === 'forecast'
  );
}
